/**
 * AI Settings Service
 * Handles API calls for AI mode configuration per analysis
 */
import api from '../api/apiClient';

export const aiSettingsService = {
    /**
     * Get Ollama server status and available models
     * @returns {Promise<{success: boolean, data: {available: boolean, models: Array, endpoint: string}}>}
     */
    getOllamaStatus: async () => {
        try {
            const response = await api.get('/ai/ollama-status');
            return response.data;
        } catch (error) {
            console.error('Error checking Ollama status:', error);
            return {
                success: false,
                data: {
                    available: false,
                    models: [],
                    error: error.message
                }
            };
        }
    },

    /**
     * Get overall AI service status
     * @returns {Promise<Object>}
     */
    getAIServiceStatus: async () => {
        try {
            const response = await api.get('/ai/status');
            return response.data;
        } catch (error) {
            console.error('Error getting AI service status:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Get AI settings for a specific analysis
     * @param {string} analysisId 
     * @returns {Promise<Object>}
     */
    getAnalysisAISettings: async (analysisId) => {
        try {
            const response = await api.get(`/ai/analyses/${analysisId}/settings`);
            return response.data;
        } catch (error) {
            console.error(`Error getting AI settings for analysis ${analysisId}:`, error);
            return {
                success: false,
                data: { settings: { mode: 'external' } },
                error: error.message
            };
        }
    },

    /**
     * Update AI mode for a single analysis
     * @param {string} analysisId 
     * @param {string} aiMode - 'external' or 'local'
     * @param {string} localModel - Ollama model name (optional)
     * @returns {Promise<Object>}
     */
    updateAnalysisAIMode: async (analysisId, aiMode, localModel = 'gemma3:12b') => {
        try {
            const response = await api.put(`/ai/analyses/${analysisId}/settings`, {
                mode: aiMode,
                localModel
            });
            return response.data;
        } catch (error) {
            console.error(`Error updating AI mode for analysis ${analysisId}:`, error);
            throw error;
        }
    },

    /**
     * Batch update AI mode for multiple analyses
     * @param {Array<{analysisId: string, aiMode: string, localModel?: string}>} updates 
     * @returns {Promise<Object>}
     */
    batchUpdateAIMode: async (updates) => {
        try {
            const response = await api.put('/ai/batch-settings', { updates });
            return response.data;
        } catch (error) {
            console.error('Error batch updating AI modes:', error);
            throw error;
        }
    },

    /**
     * Get all analyses with their AI settings (for admin)
     * @returns {Promise<Object>}
     */
    getAllAnalysesAISettings: async () => {
        try {
            const response = await api.get('/ai/analyses-settings');
            return response.data;
        } catch (error) {
            console.error('Error getting all analyses AI settings:', error);
            return { success: false, data: [], error: error.message };
        }
    }
};

export default aiSettingsService;
